import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete, Description } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { VendorFormDialog } from './VendorFormDialog';
import { VendorDocumentsDialog } from './VendorDocumentsDialog';
import { vendorsApi, type Vendor, type CreateVendorInput } from '../../api/vendors.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function VendorListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [deleting, setDeleting] = useState<Vendor | null>(null);
  const [docsVendor, setDocsVendor] = useState<Vendor | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['vendors', page, limit, search],
    queryFn: () => vendorsApi.list({ page, limit, search: search || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['vendors'] });

  const createMut = useMutation({
    mutationFn: (input: CreateVendorInput) => vendorsApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateVendorInput }) => vendorsApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => vendorsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Vendor>[] = [
    { key: 'vendorCode', label: 'Code', width: 110 },
    { key: 'vendorName', label: 'Name' },
    { key: 'serviceCategory', label: 'Category' },
    { key: 'gstNumber', label: 'GST' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : r.status === 'blacklisted' ? 'error' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader
        title="Vendor Master"
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="vendors" permission="vendor.export" filters={{ search }} />
            {hasPermission('vendor.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Vendor</Button>}
          </Stack>
        }
      />
      <Stack direction="row" mb={2}>
        <TextField size="small" placeholder="Search name / code / GST" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Documents"><IconButton size="small" onClick={() => setDocsVendor(r)}><Description fontSize="small" /></IconButton></Tooltip>
            {hasPermission('vendor.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('vendor.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <VendorFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <VendorDocumentsDialog vendorId={docsVendor?.id ?? null} vendorName={docsVendor?.vendorName} onClose={() => setDocsVendor(null)} />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete vendor"
        message={`Soft-delete ${deleting?.vendorName}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
